import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiImage, FiX, FiChevronLeft, FiChevronRight, FiArrowRight } from 'react-icons/fi';
import { getImageUrl } from '../../utils/imageUtils';
import { formatarData } from '../../utils/dateUtils';

const GaleriaFotos = () => {
  const [viagensComFotos, setViagensComFotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [albumAberto, setAlbumAberto] = useState(null);
  const [fotosAlbum, setFotosAlbum] = useState([]);
  const [viagemAlbum, setViagemAlbum] = useState(null);
  const [loadingFotos, setLoadingFotos] = useState(false);
  const [fotoSelecionada, setFotoSelecionada] = useState(null);
  const [indiceAtual, setIndiceAtual] = useState(0);

  useEffect(() => {
    fetchViagensComFotos();
  }, []);

  const fetchViagensComFotos = async () => {
    try {
      const response = await axios.get('/api/site/galeria-fotos/viagens');
      setViagensComFotos(response.data);
    } catch (error) {
      console.error('Erro ao carregar viagens com fotos:', error);
    } finally {
      setLoading(false);
    }
  };

  const abrirAlbum = async (viagem) => {
    setAlbumAberto(viagem.id);
    setViagemAlbum(viagem);
    setLoadingFotos(true);
    
    try {
      const response = await axios.get(`/api/site/galeria-fotos/viagem/${viagem.id}`);
      setFotosAlbum(response.data);
    } catch (error) {
      console.error('Erro ao carregar fotos do álbum:', error);
    } finally {
      setLoadingFotos(false);
    }
  };

  const fecharAlbum = () => {
    setAlbumAberto(null);
    setFotosAlbum([]);
    setViagemAlbum(null);
    setFotoSelecionada(null);
  };

  const abrirFoto = (foto, index) => {
    setFotoSelecionada(foto);
    setIndiceAtual(index);
  };

  const proximaFoto = () => {
    const novoIndice = (indiceAtual + 1) % fotosAlbum.length;
    setIndiceAtual(novoIndice);
    setFotoSelecionada(fotosAlbum[novoIndice]);
  };

  const fotoAnterior = () => {
    const novoIndice = (indiceAtual - 1 + fotosAlbum.length) % fotosAlbum.length;
    setIndiceAtual(novoIndice);
    setFotoSelecionada(fotosAlbum[novoIndice]);
  };

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-sky-600 to-sky-500 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-5xl font-bold mb-4">Galeria de Fotos</h1>
          <p className="text-xl text-sky-100">Conheça nossos destinos através das imagens</p>
        </div>
      </section>

      {/* Viagens com Fotos */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
            </div>
          ) : viagensComFotos.length === 0 ? (
            <div className="text-center py-12">
              <FiImage className="mx-auto text-gray-400 mb-4" size={64} />
              <p className="text-gray-600 text-lg">Nenhuma viagem com fotos disponível no momento.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {viagensComFotos.map((viagem) => (
                <div
                  key={viagem.id}
                  className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
                  onClick={() => abrirAlbum(viagem)}
                >
                  <div className="relative h-64 bg-gradient-to-br from-sky-400 to-sky-600">
                    {viagem.capa_url ? (
                      <>
                        <img
                          src={getImageUrl(viagem.capa_url)}
                          alt={`Capa do álbum ${viagem.titulo}`}
                          className="absolute inset-0 w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
                      </>
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <FiImage className="text-white/50" size={64} />
                      </div>
                    )}
                    <div className="absolute bottom-4 left-4 right-4 z-10">
                      <div className="bg-white/90 backdrop-blur-sm rounded-lg p-3">
                        <p className="text-lg font-bold text-gray-900">{viagem.titulo}</p>
                        <p className="text-sm text-gray-600">{viagem.destino}</p>
                        <p className="text-xs text-gray-500 mt-1">{formatarData(viagem.data_ida)}</p>
                      </div>
                    </div>
                    <div className="absolute top-4 right-4 z-10 bg-sky-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {viagem.total_fotos} {viagem.total_fotos === 1 ? 'foto' : 'fotos'}
                    </div>
                  </div>
                  <div className="p-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        abrirAlbum(viagem);
                      }}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors"
                    >
                      Ver Álbum
                      <FiArrowRight size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Modal do Álbum */}
      {albumAberto && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 overflow-y-auto"
          onClick={fecharAlbum}
        >
          <div
            className="bg-white rounded-lg max-w-6xl w-full p-6 my-8 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-3xl font-bold text-gray-900">{viagemAlbum?.titulo}</h2>
                {viagemAlbum && (
                  <div className="mt-2 text-gray-600">
                    <p>{viagemAlbum.destino}</p>
                    <p className="text-sm">{formatarData(viagemAlbum.data_ida)}</p>
                  </div>
                )}
              </div>
              <button
                onClick={fecharAlbum}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX size={28} />
              </button>
            </div>

            {loadingFotos ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
              </div>
            ) : fotosAlbum.length === 0 ? (
              <div className="text-center py-12">
                <FiImage className="mx-auto text-gray-400 mb-4" size={64} />
                <p className="text-gray-600 text-lg">Nenhuma foto disponível para esta viagem.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {fotosAlbum.map((foto, index) => (
                  <div
                    key={foto.id}
                    className="relative aspect-square bg-gray-200 rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity group"
                    onClick={() => abrirFoto(foto, index)}
                  >
                    <img
                      src={getImageUrl(foto.url)}
                      alt={foto.titulo || 'Foto'}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <FiImage className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={32} />
                    </div>
                    {foto.titulo && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                        <p className="text-white text-sm font-medium">{foto.titulo}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de Foto Individual */}
      {fotoSelecionada && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-[60] p-4"
          onClick={() => setFotoSelecionada(null)}
        >
          <div className="relative max-w-6xl w-full max-h-[90vh]">
            <button
              onClick={() => setFotoSelecionada(null)}
              className="absolute top-4 right-4 z-10 bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-colors"
            >
              <FiX size={24} />
            </button>
            {fotosAlbum.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    fotoAnterior();
                  }}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-colors"
                >
                  <FiChevronLeft size={24} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    proximaFoto();
                  }}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-white/20 hover:bg-white/30 text-white p-3 rounded-full transition-colors"
                >
                  <FiChevronRight size={24} />
                </button>
              </>
            )}
            <img
              src={getImageUrl(fotoSelecionada.url)}
              alt={fotoSelecionada.titulo || 'Foto'}
              className="max-w-full max-h-[90vh] mx-auto object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            {fotoSelecionada.titulo && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white/90 px-6 py-3 rounded-lg">
                <p className="text-gray-900 font-medium text-center">{fotoSelecionada.titulo}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GaleriaFotos;
